import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { 
  X, FileText, Clock, Calendar, User, AlertTriangle, 
  CheckCircle, Save, Download, MessageSquare,
  Tag, Heart, ClipboardList, Loader2, Plus, X as XIcon
} from 'lucide-react';

export default function ChatSummary({ sessionId, onClose, isOpen }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [newTopic, setNewTopic] = useState('');
  
  // Form data for manual entry
  const [formData, setFormData] = useState({
    key_topics: [],
    mood_emotional_state: '',
    risk_level: 'low',
    recommendations: '',
    follow_up_actions: '',
    provider_notes: ''
  });
  
  const printRef = useRef();

  useEffect(() => {
    if (isOpen && sessionId) {
      fetchData();
    }
  }, [isOpen, sessionId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Try to fetch existing summary
      try {
        const summaryRes = await axios.get(`${API}/sessions/${sessionId}/summary`, { headers });
        setSummary(summaryRes.data);
        setFormData({
          key_topics: summaryRes.data.key_topics || [],
          mood_emotional_state: summaryRes.data.mood_emotional_state || '',
          risk_level: summaryRes.data.risk_level || 'low',
          recommendations: summaryRes.data.recommendations || '',
          follow_up_actions: summaryRes.data.follow_up_actions || '',
          provider_notes: summaryRes.data.provider_notes || ''
        });
        setTranscript(summaryRes.data.transcript || []);
        setSessionInfo(summaryRes.data);
      } catch (err) {
        if (err.response?.status === 404) {
          // No summary yet, fetch session info and messages
          const [sessionRes, messagesRes] = await Promise.all([
            axios.get(`${API}/sessions/${sessionId}`, { headers }),
            axios.get(`${API}/messages/${sessionId}`, { headers })
          ]);
          setSessionInfo(sessionRes.data);
          setTranscript(messagesRes.data.map(m => ({
            sender_role: m.sender_role,
            content: m.content,
            timestamp: m.timestamp
          })));
        } else {
          throw err;
        }
      }
    } catch (error) {
      toast.error('Failed to load session data');
    } finally {
      setLoading(false);
    }
  };

  const addTopic = () => {
    if (newTopic.trim() && !formData.key_topics.includes(newTopic.trim())) {
      setFormData({
        ...formData,
        key_topics: [...formData.key_topics, newTopic.trim()]
      });
      setNewTopic('');
    }
  };

  const removeTopic = (topic) => {
    setFormData({
      ...formData,
      key_topics: formData.key_topics.filter(t => t !== topic)
    });
  };

  const saveSummary = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (summary) {
        // Update existing summary
        await axios.put(`${API}/sessions/${sessionId}/summary`, formData, { headers });
        toast.success('Summary updated');
      } else {
        // Create new summary (manual)
        await axios.post(`${API}/sessions/${sessionId}/summary/manual`, {
          ...formData,
          session_id: sessionId
        }, { headers });
        toast.success('Summary saved');
      }
      
      // Refresh data
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save summary');
    } finally {
      setSaving(false);
    }
  };

  const exportPDF = () => {
    const content = printRef.current;
    if (!content) return;

    const sessionDate = sessionInfo?.session_date || sessionInfo?.created_at?.split('T')[0] || 'N/A';
    const sessionTime = sessionInfo?.session_time || sessionInfo?.created_at?.split('T')[1]?.slice(0,5) || 'N/A';
    const maskedId = sessionInfo?.masked_id || summary?.masked_id || `ANON-${sessionId?.slice(0,8).toUpperCase()}`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Chat Summary - ${maskedId}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #2D3748; border-bottom: 2px solid #68D391; padding-bottom: 10px; }
            h2 { color: #4A5568; margin-top: 24px; font-size: 16px; }
            .header-info { display: flex; gap: 24px; margin-bottom: 24px; color: #718096; flex-wrap: wrap; }
            .header-info span { background: #F7FAFC; padding: 4px 12px; border-radius: 4px; }
            .section { margin: 20px 0; padding: 16px; background: #F7FAFC; border-radius: 8px; }
            .risk-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-weight: bold; }
            .risk-low { background: #C6F6D5; color: #22543D; }
            .risk-medium { background: #FEEBC8; color: #744210; }
            .risk-high { background: #FED7D7; color: #742A2A; }
            .topics { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
            .topic { background: #E2E8F0; padding: 4px 12px; border-radius: 16px; font-size: 14px; }
            .transcript { margin-top: 24px; }
            .message { padding: 8px 12px; margin: 4px 0; border-radius: 8px; }
            .message-seeker { background: #E2E8F0; margin-right: 20%; }
            .message-provider { background: #C6F6D5; margin-left: 20%; }
            .message-role { font-weight: bold; color: #4A5568; font-size: 12px; }
            .message-time { font-size: 11px; color: #A0AEC0; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #E2E8F0; color: #A0AEC0; font-size: 12px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>Session Summary Report</h1>
          <div class="header-info">
            <span><strong>Client ID:</strong> ${maskedId}</span>
            <span><strong>Date:</strong> ${sessionDate}</span>
            <span><strong>Time:</strong> ${sessionTime}</span>
            <span><strong>Grade:</strong> ${sessionInfo?.grade_code || 'Chat'}</span>
          </div>
          
          <div class="section">
            <h2>Risk Assessment</h2>
            <span class="risk-badge risk-${formData.risk_level}">${formData.risk_level.toUpperCase()}</span>
          </div>
          
          <div class="section">
            <h2>Key Topics Discussed</h2>
            <div class="topics">
              ${formData.key_topics.length > 0 
                ? formData.key_topics.map(t => `<span class="topic">${t}</span>`).join('') 
                : '<em>No topics recorded</em>'}
            </div>
          </div>
          
          <div class="section">
            <h2>Emotional State Observed</h2>
            <p>${formData.mood_emotional_state || '<em>Not recorded</em>'}</p>
          </div>
          
          <div class="section">
            <h2>Clinical Recommendations</h2>
            <p>${formData.recommendations || '<em>No recommendations provided</em>'}</p>
          </div>
          
          <div class="section">
            <h2>Follow-up Actions</h2>
            <p>${formData.follow_up_actions || '<em>No follow-up actions specified</em>'}</p>
          </div>
          
          <div class="section">
            <h2>Provider Notes</h2>
            <p>${formData.provider_notes || '<em>No additional notes</em>'}</p>
          </div>
          
          <div class="transcript">
            <h2>Session Transcript (${transcript.length} messages)</h2>
            ${transcript.map(m => `
              <div class="message ${m.sender_role === 'care_seeker' ? 'message-seeker' : 'message-provider'}">
                <span class="message-role">${m.sender_role === 'care_seeker' ? 'Client' : 'Provider'}</span>
                <span class="message-time"> - ${new Date(m.timestamp).toLocaleTimeString()}</span>
                <p style="margin: 4px 0 0 0;">${m.content}</p>
              </div>
            `).join('')}
          </div>
          
          <div class="footer">
            <p>Generated by Veyonika | ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
            <p>This document contains confidential patient information.</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-orange-100 text-orange-700 border-orange-300';
      default: return 'bg-green-100 text-green-700 border-green-300';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-emerald-50">
          <div className="flex items-center gap-3">
            <FileText className="text-blue-600" size={28} />
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Session Summary</h2>
              <p className="text-sm text-gray-500">
                Client: {sessionInfo?.masked_id || summary?.masked_id || `ANON-${sessionId?.slice(0,8).toUpperCase()}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" ref={printRef}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-blue-500" size={40} />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Session Info Bar */}
              <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-xl text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar size={16} />
                  <span>{sessionInfo?.session_date || sessionInfo?.created_at?.split('T')[0] || 'Today'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock size={16} />
                  <span>{sessionInfo?.session_time || sessionInfo?.created_at?.split('T')[1]?.slice(0,5) || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <MessageSquare size={16} />
                  <span>{transcript.length} messages</span>
                </div>
                {sessionInfo?.grade_code && (
                  <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    {sessionInfo.grade_code}
                  </div>
                )}
              </div>

              {/* Risk Level */}
              <div>
                <h3 className="flex items-center gap-2 text-gray-700 font-medium mb-3">
                  <AlertTriangle size={18} />
                  Risk Level Assessment
                </h3>
                <div className="flex gap-3">
                  {['low', 'medium', 'high'].map((level) => (
                    <button
                      key={level}
                      onClick={() => setFormData({ ...formData, risk_level: level })}
                      className={`px-4 py-2 rounded-full font-medium border transition-all ${
                        formData.risk_level === level
                          ? getRiskColor(level)
                          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {level === 'high' && <AlertTriangle size={14} className="inline mr-1" />}
                      {level === 'low' && <CheckCircle size={14} className="inline mr-1" />}
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Key Topics */}
              <div>
                <h3 className="flex items-center gap-2 text-gray-700 font-medium mb-3">
                  <Tag size={18} />
                  Key Topics Discussed
                </h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.key_topics.map((topic, idx) => (
                    <span 
                      key={idx} 
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2"
                    >
                      {topic}
                      <button 
                        onClick={() => removeTopic(topic)}
                        className="hover:bg-blue-200 rounded-full p-0.5"
                      >
                        <XIcon size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTopic}
                    onChange={(e) => setNewTopic(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTopic()}
                    placeholder="Add a topic (e.g., anxiety, relationships)"
                    className="flex-1 px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  <button
                    onClick={addTopic}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                {/* Quick topic suggestions */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {['Anxiety', 'Depression', 'Stress', 'Relationships', 'Work', 'Sleep', 'Self-esteem', 'Grief'].map((topic) => (
                    !formData.key_topics.includes(topic) && (
                      <button
                        key={topic}
                        onClick={() => setFormData({ ...formData, key_topics: [...formData.key_topics, topic] })}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                      >
                        + {topic}
                      </button>
                    )
                  ))}
                </div>
              </div>

              {/* Emotional State */}
              <div>
                <h3 className="flex items-center gap-2 text-gray-700 font-medium mb-3">
                  <Heart size={18} />
                  Emotional State Observed
                </h3>
                <textarea
                  value={formData.mood_emotional_state}
                  onChange={(e) => setFormData({ ...formData, mood_emotional_state: e.target.value })}
                  placeholder="Describe the client's emotional state during the session (e.g., anxious but cooperative, tearful at times, hopeful towards the end)"
                  className="w-full p-4 border rounded-xl min-h-[80px] text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {/* Recommendations */}
              <div>
                <h3 className="flex items-center gap-2 text-gray-700 font-medium mb-3">
                  <ClipboardList size={18} />
                  Clinical Recommendations
                </h3>
                <textarea
                  value={formData.recommendations}
                  onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                  placeholder="Your therapeutic recommendations (e.g., Continue weekly sessions, practice mindfulness exercises, consider referral to psychiatrist)"
                  className="w-full p-4 border rounded-xl min-h-[80px] text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {/* Follow-up Actions */}
              <div>
                <h3 className="flex items-center gap-2 text-gray-700 font-medium mb-3">
                  <CheckCircle size={18} />
                  Follow-up Actions
                </h3>
                <textarea
                  value={formData.follow_up_actions}
                  onChange={(e) => setFormData({ ...formData, follow_up_actions: e.target.value })}
                  placeholder="Next steps and follow-up actions (e.g., Schedule follow-up in 1 week, assign journaling homework, send resources)"
                  className="w-full p-4 border rounded-xl min-h-[80px] text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {/* Provider Notes */}
              <div>
                <h3 className="flex items-center gap-2 text-gray-700 font-medium mb-3">
                  <FileText size={18} />
                  Additional Provider Notes
                </h3>
                <textarea
                  value={formData.provider_notes}
                  onChange={(e) => setFormData({ ...formData, provider_notes: e.target.value })}
                  placeholder="Any additional clinical observations or notes for your records..."
                  className="w-full p-4 border rounded-xl min-h-[100px] text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              {/* Transcript Preview */}
              {transcript.length > 0 && (
                <div>
                  <h3 className="flex items-center gap-2 text-gray-700 font-medium mb-3">
                    <MessageSquare size={18} />
                    Session Transcript ({transcript.length} messages)
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-4 max-h-60 overflow-y-auto space-y-3">
                    {transcript.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.sender_role === 'care_seeker' ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-[80%] p-3 rounded-lg ${
                          msg.sender_role === 'care_seeker' 
                            ? 'bg-white border border-gray-200' 
                            : 'bg-emerald-100'
                        }`}>
                          <p className="text-xs font-medium text-gray-500 mb-1">
                            {msg.sender_role === 'care_seeker' ? 'Client' : 'Provider'}
                            <span className="ml-2 text-gray-400">
                              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </p>
                          <p className="text-sm text-gray-700">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
          <p className="text-sm text-gray-500">
            {summary ? 'Last saved: ' + new Date(summary.updated_at || summary.created_at).toLocaleString() : 'Not saved yet'}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={exportPDF}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
            >
              <Download size={18} />
              Export PDF
            </button>
            <button
              onClick={saveSummary}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Summary
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
